import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { Cookie, ShieldCheck } from "lucide-react";

const CONSENT_KEY = "bicenter_cookie_consent_v1";

export const CookieBanner = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      // Show after 800ms so it doesn't compete with hero
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const persist = (value) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({
      choice: value,
      timestamp: new Date().toISOString(),
      version: "v1",
    }));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-[80]"
    >
      <div className="border-gradient-primary p-[1px] rounded-2xl shadow-[0_20px_60px_-10px_rgba(0,0,0,0.6)]">
        <div className="bg-[#0c0c0e] rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <Cookie size={18} className="text-bicenter-cyan" />
            <p className="text-sm font-display uppercase tracking-[0.28em] text-white">
              {t.cookie.title}
            </p>
          </div>
          <p className="text-sm text-zinc-300 leading-relaxed">
            {t.cookie.body}
          </p>
          <p className="text-xs text-zinc-500 mt-3 flex items-center gap-1.5">
            <ShieldCheck size={12} className="text-bicenter-cyan" />
            {t.cookie.compliance}
          </p>
          <div className="mt-5 flex flex-col sm:flex-row gap-2">
            <Button
              onClick={() => persist("essential-only")}
              data-testid="cookie-refuse"
              variant="outline"
              className="flex-1 border-white/15 text-white hover:bg-white/5 h-11"
            >
              {t.cookie.refuse}
            </Button>
            <Button
              onClick={() => persist("all")}
              data-testid="cookie-accept"
              className="flex-1 bg-gradient-to-r from-bicenter-cyan to-bicenter-blue text-white h-11 hover:shadow-[0_0_20px_rgba(0,169,255,0.4)]"
            >
              {t.cookie.accept}
            </Button>
          </div>
          <a
            href="/legal#cookies"
            data-testid="cookie-learn-more"
            className="mt-3 inline-block text-xs text-zinc-400 hover:text-white underline underline-offset-2"
          >
            {t.cookie.learn_more}
          </a>
        </div>
      </div>
    </div>
  );
};
